import { Fragment, PureComponent, ReactNode } from 'react';
import {
    components,
    OptionProps,
    ValueContainerProps,
    ControlProps,
    MenuProps,
    StylesConfig,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import Fuse from 'fuse.js';
import { CSSObject } from '@emotion/serialize';

import { ONE_CHAR_GENDER } from '../pages/NewPatient';

import { getAllPatients } from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import { AntTooltip } from './common/AntTooltip';
import { AbhaIconNotLinked, EkaCareIcon } from './common/Icons';

type SuggestionItem = Fuse.FuseResult<Patient & { __isNew__?: boolean }>;

const fuseConfig = {
    keys: ['personal.name.f', 'personal.name.l'],
    threshold: 0.2,
};

const Option = (props: OptionProps<SuggestionItem, false>) => {
    const { data, selectProps } = props;
    const { isPhCode } = selectProps;

    const ptname = [data.item.personal.name.f, data.item.personal.name.l].filter(Boolean).join(' ');

    return (
        <components.Option {...props}>
            <div className="flex items-center justify-between font-600 text-neutral-3">
                {data.item.__isNew__ ? (
                    <p className="text-actionPrimary">+ Add another patient with {ptname}</p>
                ) : (
                    <Fragment>
                        <div className="flex flex-wrap items-center w-75p">
                            <div className="flex items-center break-all">
                                {data.item.personal.onApp && (
                                    <AntTooltip content="Connected on eka.care app">
                                        <span className="top-1 right-1 relative mr-3 w-16 h-16">
                                            <EkaCareIcon />
                                        </span>
                                    </AntTooltip>
                                )}
                                {data?.item?.abha?.id && (
                                    <AntTooltip content="ABHA address is present">
                                        {AbhaIconNotLinked}
                                    </AntTooltip>
                                )}
                                <span>{`${ptname}, `}</span>
                            </div>
                            <span>{`${
                                isPhCode && data.item.personal.phone?.c
                                    ? `${data.item.personal.phone?.c} `
                                    : ' '
                            }${data.item.personal.phone?.n || ''}`}</span>
                        </div>
                        <div>
                            {ONE_CHAR_GENDER[data.item.personal.gender.toLowerCase()]}
                            {data.item.personal.age?.value
                                ? `, ${data.item.personal.age?.value} Y`
                                : ''}
                        </div>
                    </Fragment>
                )}
            </div>
        </components.Option>
    );
};

const ValueContainer = (props: ValueContainerProps<SuggestionItem, false>) => {
    const { hasValue, children, getValue, setValue, selectProps } = props;
    const { isDisabled, isModal } = selectProps;
    if (hasValue) {
        const value: Patient = getValue()[0].item;
        return isModal ? (
            <div className="relative flex items-center justify-between gap-8 py-10 px-12">
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        width="13"
                        height="15"
                        viewBox="0 0 13 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M7.8125 9.0625C10.4648 9.0625 12.625 11.2227 12.625 13.875C12.625 14.3672 12.2148 14.75 11.75 14.75H1.25C0.757812 14.75 0.375 14.3672 0.375 13.875C0.375 11.2227 2.50781 9.0625 5.1875 9.0625H7.8125ZM1.6875 13.4375H11.2852C11.0664 11.7148 9.58984 10.375 7.8125 10.375H5.1875C3.38281 10.375 1.90625 11.7148 1.6875 13.4375ZM6.5 7.75C4.55859 7.75 3 6.19141 3 4.25C3 2.33594 4.55859 0.75 6.5 0.75C8.41406 0.75 10 2.33594 10 4.25C10 6.19141 8.41406 7.75 6.5 7.75ZM6.5 2.0625C5.26953 2.0625 4.3125 3.04688 4.3125 4.25C4.3125 5.48047 5.26953 6.4375 6.5 6.4375C7.70312 6.4375 8.6875 5.48047 8.6875 4.25C8.6875 3.04688 7.70312 2.0625 6.5 2.0625Z"
                            fill="#3E445C"
                        />
                    </svg>
                </div>
                <input
                    type="text"
                    value={[value.personal.name.f, value.personal.name.l].filter(Boolean).join(' ')}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                    disabled={isDisabled}
                />
                <div className="mimic-box"></div>
            </div>
        ) : (
            <div className="add-pt-input">
                <input
                    type="text"
                    value={[value.personal.name.f, value.personal.name.l].filter(Boolean).join(' ')}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        className="w-16 h-16 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                    >
                        <path d="M272 304h-96C78.8 304 0 382.8 0 480c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32C448 382.8 369.2 304 272 304zM48.99 464C56.89 400.9 110.8 352 176 352h96c65.16 0 119.1 48.95 127 112H48.99zM224 256c70.69 0 128-57.31 128-128c0-70.69-57.31-128-128-128S96 57.31 96 128C96 198.7 153.3 256 224 256zM224 48c44.11 0 80 35.89 80 80c0 44.11-35.89 80-80 80S144 172.1 144 128C144 83.89 179.9 48 224 48z"></path>
                    </svg>
                </div>
                <div className="mimic-box"></div>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<SuggestionItem, false>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const Menu = (props: MenuProps<SuggestionItem, false>) => {
    const { children, options } = props;
    if (options.length === 0) {
        return null;
    }

    return <components.Menu {...props}>{children}</components.Menu>;
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { selectProps, onChange, onFocus, value } = props;
    const {
        placeholder,
        onPhoneNumberBlur,
        isPhoneInputError,
        onInputChange,
        isDisabled,
        isModal,
    } = selectProps;

    const onCChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(e);
    };

    return isModal ? (
        <div
            className={`relative flex items-center justify-between gap-8 py-10 px-12 ${
                isPhoneInputError ? 'error' : ''
            }`}
        >
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    width="13"
                    height="15"
                    viewBox="0 0 13 15"
                    xmlns="http://www.w3.org/2000/svg"
                    fill={isDisabled ? '#8D95B5' : '#3E445C'}
                >
                    <path d="M7.8125 9.0625C10.4648 9.0625 12.625 11.2227 12.625 13.875C12.625 14.3672 12.2148 14.75 11.75 14.75H1.25C0.757812 14.75 0.375 14.3672 0.375 13.875C0.375 11.2227 2.50781 9.0625 5.1875 9.0625H7.8125ZM1.6875 13.4375H11.2852C11.0664 11.7148 9.58984 10.375 7.8125 10.375H5.1875C3.38281 10.375 1.90625 11.7148 1.6875 13.4375ZM6.5 7.75C4.55859 7.75 3 6.19141 3 4.25C3 2.33594 4.55859 0.75 6.5 0.75C8.41406 0.75 10 2.33594 10 4.25C10 6.19141 8.41406 7.75 6.5 7.75ZM6.5 2.0625C5.26953 2.0625 4.3125 3.04688 4.3125 4.25C4.3125 5.48047 5.26953 6.4375 6.5 6.4375C7.70312 6.4375 8.6875 5.48047 8.6875 4.25C8.6875 3.04688 7.70312 2.0625 6.5 2.0625Z" />
                </svg>
            </div>
            <input
                type="text"
                pattern="\d+"
                placeholder={placeholder}
                className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onPhoneNumberBlur}
                disabled={isDisabled}
            />
            <div className="mimic-box"></div>
        </div>
    ) : (
        <div className={`add-pt-input ${isPhoneInputError ? 'error' : ''}`}>
            <input
                type="text"
                pattern="\d+"
                placeholder={placeholder}
                className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onPhoneNumberBlur}
                disabled={isDisabled}
            />
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    className="w-16 h-16 fill-neutral-5"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                >
                    <path d="M272 304h-96C78.8 304 0 382.8 0 480c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32C448 382.8 369.2 304 272 304zM48.99 464C56.89 400.9 110.8 352 176 352h96c65.16 0 119.1 48.95 127 112H48.99zM224 256c70.69 0 128-57.31 128-128c0-70.69-57.31-128-128-128S96 57.31 96 128C96 198.7 153.3 256 224 256zM224 48c44.11 0 80 35.89 80 80c0 44.11-35.89 80-80 80S144 172.1 144 128C144 83.89 179.9 48 224 48z"></path>
                </svg>
            </div>
            <div className="mimic-box"></div>
        </div>
    );
};

const DropdownIndicator = () => null;
const NoOptionsMessage = () => null;
const LoadingIndicator = () => null;
const ClearIndicator = () => null;
const IndicatorsContainer = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Option,
    NoOptionsMessage,
    LoadingIndicator,
    Menu,
    ClearIndicator,
    IndicatorsContainer,
};

const customStyles: StylesConfig<SuggestionItem, false> = {
    menu: (base) => {
        return {
            ...base,
            paddingTop: 0,
        } as CSSObject;
    },
};

type Props = {
    inputValue: string;
    onSelect: (p?: Patient) => void;
    onInputChange: (text: string) => void;
    disabled: boolean;
    suggestionsEnabled: boolean;
    isModal?: boolean;
    className?: string;
    isPhCode: boolean | undefined;
};

type State = {
    suggestedPatients: Patient[];
    menuIsOpen: boolean;
};

class PatientNameAutoSuggest extends PureComponent<Props, State> {
    patientsFuse = new Fuse([] as Patient[], fuseConfig);

    state: State = {
        suggestedPatients: [],
        menuIsOpen: false,
    };

    constructor(props: Props) {
        super(props);

        getAllPatients()
            .then((res) => {
                const allPt = res?.filter((t) => !t.archived) || [];
                this.patientsFuse = new Fuse(allPt, fuseConfig);
            })
            .catch(console.error);
    }

    onPhoneNumberChange2 = (input: string): void => {
        this.setState({
            menuIsOpen: true,
        });

        const { disabled } = this.props;

        if (disabled) {
            return;
        }

        this.props.onInputChange(input);
    };

    onChange = (patient: SuggestionItem | null): void => {
        const { onSelect } = this.props;

        if (patient === null) {
            onSelect();
            return;
        }

        onSelect(patient.item);
    };

    onPhoneNumberBlur = (): void => {
        this.setState({ menuIsOpen: false });
    };

    loadPatients = async (text: string): Promise<SuggestionItem[]> => {
        if (!this.props.suggestionsEnabled) {
            return [];
        }

        const allPatients: SuggestionItem[] = this.patientsFuse.search(text).slice(0, 10);
        if (allPatients.length > 0) {
            allPatients.push({
                item: {
                    __isNew__: true,
                    localId: new Date().getTime().toString(),
                    createdAt: new Date(),
                    personal: { name: { f: text }, phone: { c: '', n: '' }, gender: '' },
                },
                // NA
                refIndex: 0,
            });
        }
        return allPatients;
    };

    private readonly filterOption = () => true;

    render(): ReactNode {
        const { menuIsOpen } = this.state;
        const { inputValue, disabled } = this.props;

        const children = (
            <AsyncSelect
                value={[]}
                isClearable={true}
                components={customComponents}
                styles={customStyles}
                loadOptions={this.loadPatients}
                onInputChange={this.onPhoneNumberChange2}
                inputValue={inputValue}
                onChange={this.onChange}
                onPhoneNumberBlur={this.onPhoneNumberBlur}
                filterOption={this.filterOption}
                menuIsOpen={menuIsOpen}
                tabSelectsValue={false}
                isDisabled={disabled}
                placeholder={this.props.isModal ? 'Enter Patient Name' : 'Firstname Lastname'}
                isModal={this.props.isModal}
                isPhCode={this.props.isPhCode}
            />
        );

        return this.props.isModal ? (
            <div
                className={`space-y-4 w-full bg-darwin-neutral-50 ${
                    disabled ? 'text-darwin-neutral-500' : ''
                }`}
            >
                {children}
            </div>
        ) : (
            <div className={this.props.className ?? ''}>{children}</div>
        );
    }
}

export default PatientNameAutoSuggest;
